import React from 'react';
import { Tag } from 'antd';
import { GroupedMaterialReservation } from './types';
import {
	QUEUE_CLASS_LABELS,
	reasonDetail,
	reasonLabel,
	reasonLabels,
	sharedBarChipLabel,
} from './queueClassification';

interface Props {
	record: GroupedMaterialReservation;
	/** Detail view has more room, so its tags are a step larger. */
	size?: 'card' | 'detail';
}

const MAX_SHARED_BAR_CHIPS = 2;

/**
 * The saw queue classification of a group: what it is, and why it is held back.
 * Shared by the queue card and the cut detail page so an operator reads the
 * same wording before and after tapping into a job.
 */
const QueueClassTags: React.FC<Props> = ({ record, size = 'card' }) => {
	const queueClass = record.queue_class;
	const classFont = size === 'detail' ? 17 : 15;
	const moreMaterialTag = record.floor_generated ? (
		<Tag
			data-testid="fastlane-more-material"
			color="gold"
			style={{ fontSize: classFont, marginInlineEnd: 0 }}
		>
			More material
		</Tag>
	) : null;
	if (!queueClass) {
		return moreMaterialTag ? (
			<div className="flex flex-wrap items-center gap-1">{moreMaterialTag}</div>
		) : null;
	}

	const classLabel = QUEUE_CLASS_LABELS[queueClass];
	// Why it is held back matters more than why it is ready, so blocking and
	// review reasons get a tag and priority reasons get none: the class already
	// says the row is ready, and naming the reason again costs a tag.
	//
	// The reasons that stop work are pulled out of the tag row and written as
	// sentences below it. A row an operator cannot act on has to say what the
	// condition is and who clears it, which does not fit in a tag; a row they
	// can act on stays a tag so the card does not grow for every warning.
	const explainInline =
		queueClass === 'needs_review' || queueClass === 'blocked';
	const stopReasonCodes = [
		...(record.reconciliation_reason_codes ?? []),
		...(record.blocking_reason_codes ?? []),
	];
	const stoppedReasons = explainInline
		? stopReasonCodes.map((code) => ({
				code,
				label: reasonLabel(code),
				detail: reasonDetail(code),
		  }))
		: [];
	const taggedReasons = reasonLabels([
		...(explainInline ? [] : stopReasonCodes),
		...(record.warning_codes ?? []),
	]);
	const sharedBarCount = record.same_bar_pending_group_count ?? 0;
	const blockedBarCount = record.blocked_bar_count ?? 0;
	// One chip per shared bar, because a card can share one bar with one job and
	// another bar with a different job, and a single chip would merge the two.
	// Capped at two: past that the chips are taller than the card is worth, and
	// the overflow chip still tells the operator to open the job, where the bar
	// list names every one of them.
	const sharedBarGroups = record.same_bar_groups ?? [];
	const shownSharedBars = sharedBarGroups.slice(0, MAX_SHARED_BAR_CHIPS);
	const hiddenSharedBars = sharedBarGroups.length - shownSharedBars.length;

	const reasonFont = size === 'detail' ? 15 : 13;
	const detailFont = size === 'detail' ? 15 : 14;
	// Review means the records contradict each other, which is the case an
	// operator must not resolve alone, so it gets the warmer tint of the two.
	const detailTint =
		queueClass === 'needs_review'
			? { background: '#fff2e8', border: '#ffbb96' }
			: { background: '#fafafa', border: '#e8e8e8' };

	return (
		<div className="flex flex-col gap-1">
			<div className="flex flex-wrap items-center gap-1">
				{moreMaterialTag}
				<Tag
					data-testid="fastlane-cut-queue-class"
					data-queue-class={queueClass}
					color={classLabel.color}
					style={{ fontSize: classFont, marginInlineEnd: 0 }}
				>
					{classLabel.label}
				</Tag>
				{taggedReasons.map((reason) => (
					<Tag
						key={reason}
						data-testid="fastlane-cut-queue-reason"
						color="orange"
						style={{ fontSize: reasonFont, marginInlineEnd: 0 }}
					>
						{reason}
					</Tag>
				))}
				{shownSharedBars.map((group) => (
					<Tag
						key={group.material_length_id ?? group.friendly_id}
						data-testid="fastlane-cut-shared-bar"
						data-shared-bar={group.friendly_id ?? undefined}
						color="purple"
						// Wraps, unlike every other tag here. Part names have no length
						// limit, and the card body clips what overflows it, so a tag on
						// one line would silently cut off the end of the very identity
						// the operator is meant to match against.
						style={{
							fontSize: reasonFont,
							marginInlineEnd: 0,
							whiteSpace: 'normal',
							maxWidth: '100%',
						}}
					>
						{sharedBarChipLabel(group)}
					</Tag>
				))}
				{hiddenSharedBars > 0 ? (
					<Tag
						data-testid="fastlane-cut-shared-bar-overflow"
						color="purple"
						style={{ fontSize: reasonFont, marginInlineEnd: 0 }}
					>
						+{hiddenSharedBars} more shared bar
						{hiddenSharedBars > 1 ? 's' : ''}
					</Tag>
				) : null}
				{/*
				 * The rows past the classification cap carry the count but no bar
				 * detail, so they keep the wording they had rather than losing the
				 * mark entirely.
				 */}
				{sharedBarGroups.length === 0 && sharedBarCount > 0 ? (
					<Tag
						data-testid="fastlane-cut-shared-bar"
						color="purple"
						style={{ fontSize: reasonFont, marginInlineEnd: 0 }}
					>
						Same bar +{sharedBarCount}
					</Tag>
				) : null}
				{blockedBarCount > 0 && queueClass !== 'blocked' ? (
					<Tag
						data-testid="fastlane-cut-partial-bars"
						color="gold"
						style={{ fontSize: reasonFont, marginInlineEnd: 0 }}
					>
						{blockedBarCount} bar{blockedBarCount > 1 ? 's' : ''} unusable
					</Tag>
				) : null}
			</div>
			{stoppedReasons.length > 0 ? (
				<div
					data-testid="fastlane-cut-queue-reason-detail"
					style={{
						background: detailTint.background,
						border: `1px solid ${detailTint.border}`,
						borderRadius: 6,
						padding: '6px 8px',
						fontSize: detailFont,
						lineHeight: 1.35,
						color: '#262626',
						display: 'flex',
						flexDirection: 'column',
						gap: 4,
					}}
				>
					{stoppedReasons.map((reason) => (
						<div key={reason.code} data-reason-code={reason.code}>
							<strong>{reason.label}</strong>
							{reason.detail ? ` — ${reason.detail}` : null}
						</div>
					))}
				</div>
			) : null}
		</div>
	);
};

export default QueueClassTags;
